import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Toaster } from "sonner";
import { LayoutDashboard, Users, Briefcase, ListChecks, MessageSquare, Sparkles, Inbox, LogOut, CalendarDays, Handshake, Quote, Target, BarChart3 } from "lucide-react";
import { LOGO_LIGHT } from "@/lib/brand";

const LOGO_URL = LOGO_LIGHT;

const nav = [
  { to: "/dashboard", end: true, label: "Overview", icon: LayoutDashboard, testid: "nav-overview" },
  { to: "/dashboard/contacts", label: "Contacts", icon: Users, testid: "nav-contacts" },
  { to: "/dashboard/deals", label: "Deals", icon: Briefcase, testid: "nav-deals" },
  { to: "/dashboard/investors", label: "Investors", icon: Target, testid: "nav-investors" },
  { to: "/dashboard/pipeline", label: "Pipeline", icon: BarChart3, testid: "nav-pipeline" },
  { to: "/dashboard/tasks", label: "Tasks", icon: ListChecks, testid: "nav-tasks" },
  { to: "/dashboard/calendar", label: "Calendar", icon: CalendarDays, testid: "nav-calendar" },
  { to: "/dashboard/interactions", label: "Interactions", icon: MessageSquare, testid: "nav-interactions" },
  { to: "/dashboard/messages", label: "Inbox", icon: Inbox, testid: "nav-messages" },
  { to: "/dashboard/intros", label: "Consultations", icon: Handshake, testid: "nav-intros" },
  { to: "/dashboard/endorsements", label: "Endorsements", icon: Quote, testid: "nav-endorsements" },
  { to: "/dashboard/ai", label: "AI Assistant", icon: Sparkles, testid: "nav-ai" },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-cream flex" data-testid="dashboard-shell">
      <Toaster richColors position="top-right" />
      {/* Sidebar */}
      <aside className="w-64 bg-navy text-cream flex-shrink-0 flex flex-col" data-testid="dashboard-sidebar">
        <div className="p-6 border-b border-cream/10 flex items-center gap-3">
          <img src={LOGO_URL} alt="Joseph Poche" decoding="async" className="h-16 w-16 object-contain" />
          <div className="leading-tight">
            <div className="font-serif text-cream">Joseph Poche</div>
            <div className="overline text-gold">CRM</div>
          </div>
        </div>

        {user?.is_demo && (
          <div
            data-testid="demo-mode-badge"
            className="mx-3 mt-4 bg-gold/15 border-l-2 border-gold px-3 py-2 text-xs"
          >
            <div className="overline text-gold mb-0.5">Demo mode</div>
            <div className="text-cream/85 leading-snug">
              You're viewing isolated demo data. <Link to="/dashboard/demo" className="text-gold hover:underline">Manage</Link>
            </div>
          </div>
        )}

        <nav className="flex-1 px-3 py-6 space-y-1">
          {nav.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={n.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm transition-colors ${
                    isActive
                      ? "bg-gold text-navy"
                      : "text-cream/75 hover:text-gold hover:bg-cream/5"
                  }`
                }
              >
                <Icon className="h-4 w-4" />
                {n.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="p-4 border-t border-cream/10">
          <div className="text-xs text-cream/60 mb-2">Signed in as</div>
          <div className="text-sm text-cream truncate" data-testid="sidebar-user-email">{user?.email}</div>
          <button
            onClick={onLogout}
            data-testid="sidebar-logout"
            className="mt-3 w-full flex items-center justify-center gap-2 border border-cream/20 text-cream/80 py-2 text-xs uppercase tracking-widest hover:bg-cream/5 hover:text-gold transition-colors"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 overflow-x-auto">
        <Outlet />
      </main>
    </div>
  );
}
